import AssistantChat from './AssistantChat';
import ChatHistory from './ChatHistory';
import ChatSessionHeader from './ChatSessionHeader';
import PropTypes from 'prop-types';
import bugsnagClient from '@/ui/containers/BugsnagClient';
import { useAssistantContext } from './AssistantProvider';
import { useEffect } from 'react';

export const ASSISTANT_PANEL_WIDTH = 444;

/**
 * Integrated AI Assistant panel that takes up layout space.
 *
 * This component is designed to be part of the app layout, pushing
 * main content when open rather than overlaying it.
 *
 * Chat state is managed in AssistantProvider context, so it persists
 * across React Router navigations.
 */
const AssistantPanel = ({ isOpen, onClose }) => {
    const { panelView, setPanelView } = useAssistantContext();
    const showHistory = panelView === 'history';
    const showChat = () => setPanelView('chat');

    useEffect(() => {
        try {
            const host = document.querySelector('.productfruits--container');
            const shadowRoot = host?.shadowRoot;
            if (!shadowRoot) return;

            const styleId = 'assistant-panel-offset';
            const offsetRight = `${ASSISTANT_PANEL_WIDTH + 16}px`;

            // Remove any previous override
            shadowRoot.getElementById(styleId)?.remove();

            if (isOpen) {
                const style = document.createElement('style');
                style.id = styleId;
                style.textContent = `
          .button-launcher {
            transition: right 300ms ease-out !important;
            right: ${offsetRight} !important;
          }
        `;
                shadowRoot.appendChild(style);
            }

            return () => {
                shadowRoot.getElementById(styleId)?.remove();
            };
        } catch (err) {
            bugsnagClient.notify(err);
        }
    }, [isOpen]);

    return (
        <aside
            aria-label="Spate AI assistant"
            // Off-screen when closed but still mounted; keep it out of reach.
            inert={isOpen ? undefined : ''}
            className={`
        shrink-0 transition-[width] duration-300 ease-out
        ${isOpen ? 'w-[444px]' : 'w-0'}
      `}
        >
            <div
                className={`
          fixed top-2 bottom-2 w-[436px] bg-background border-l border-border
          flex flex-col z-[2] transition-transform duration-300 ease-out rounded-xl overflow-hidden
          ${isOpen ? 'translate-x-0 right-2' : 'translate-x-full right-0'}
        `}
            >
                <ChatSessionHeader
                    onClose={onClose}
                    showHistory={showHistory}
                    onToggleHistory={() =>
                        setPanelView(showHistory ? 'chat' : 'history')
                    }
                    onBackFromHistory={showChat}
                />
                <div className="flex-1 overflow-hidden flex flex-col">
                    {showHistory ? (
                        <ChatHistory onBack={showChat} />
                    ) : (
                        <AssistantChat />
                    )}
                </div>
            </div>
        </aside>
    );
};

AssistantPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default AssistantPanel;
